// Present-spec rows for a listing variant (plan T12). Renders only the spec
// fields that are actually present — absent / blank fields are omitted, and an
// empty spec sheet (the common case: ~96.6% of variants, all Smartphones)
// renders nothing at all, so the spec block cleanly disappears.
//
// Pure + presentational so it unit-tests directly with a plain specs object.
// Rows render as DetailRows in a bordered DetailGroup (GET-266).

import React, { useMemo } from 'react';
import { View } from 'react-native';
import { useTheme } from '../../styles/ThemeProvider';
import { DetailGroup } from '../common/DetailGroup';
import { DetailRow } from '../common/DetailRow';

interface Props {
  specs: Record<string, unknown>;
}

/** "screen_size" → "Screen Size"; "os" → "Os". */
export function humanizeSpecKey(key: string): string {
  return key
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : word))
    .join(' ');
}

/** Render a spec value to a display string, or null when it carries nothing. */
export function formatSpecValue(value: unknown): string | null {
  if (value == null) return null;
  if (Array.isArray(value)) {
    const parts = value.map((v) => String(v).trim()).filter((v) => v.length > 0);
    return parts.length > 0 ? parts.join(', ') : null;
  }
  const str = String(value).trim();
  return str.length > 0 ? str : null;
}

export default function ListingSpecList({ specs }: Props) {
  const theme = useTheme();

  const rows = useMemo(
    () =>
      Object.entries(specs)
        .map(([key, value]) => ({
          key,
          label: humanizeSpecKey(key),
          value: formatSpecValue(value),
        }))
        .filter((row): row is { key: string; label: string; value: string } => row.value !== null),
    [specs]
  );

  // No present spec fields → render nothing (the spec block cleanly omits).
  if (rows.length === 0) return null;

  return (
    <View style={{ marginTop: theme.spacing.lg }}>
      <DetailGroup title="Specifications">
        {rows.map((row) => (
          <DetailRow key={row.key} label={row.label} value={row.value} />
        ))}
      </DetailGroup>
    </View>
  );
}
